import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { COMMON_PERMISSIONS } from "../../utils/helper";
import { 
  shouldBypassPermissionForModule, 
  shouldHideMenuForModule 
} from "../../configs/menuConfig";

const checkForParams = (pathTemplate, pathname) => {
    const pathParts = pathTemplate.split('/');    // Split the path template into parts
    const urlParts = pathname.split('/');        // Split the actual URL into parts

    // Initialize flags for action and id
    let isActionPresent = false;
    let isIdPresent = false;

    // Loop through each part of the path template
    pathParts.forEach((part, index) => {
        if (part.includes(":action") && urlParts[index]) {
            isActionPresent = true;   // If we find :action in the path and it's in the URL
        }
        if (part.includes(":id") && urlParts[index]) {
            isIdPresent = true;   // If we find :id in the path and it's in the URL
        }
    });

    return { isActionPresent, isIdPresent };
};

const ProtectedRoute = ({ children, module, submodule = "", path = "", access = [], allowedUserTypes = null, bypass = null }) => {
    const { pathname } = useLocation();
    const { permissions, isAuthenticated, role: userType, userInfo } = useSelector((state) => state.user);
    const user = userInfo?.user;
    
    // If not authenticated, redirect to login
    if (!isAuthenticated) {
        return <Navigate to="/auth/login" replace />;
    }

    // If user is Super Admin (0) or Workspace Admin (1), allow all access
    if (userType === 0 || userType === 1) {
        return children;
    }

    // PRIORITY 1: Check if this route should be HIDDEN for current user type
    // Hide has highest priority - user can't access even with permission
    if (module && submodule && shouldHideMenuForModule(module, submodule, userType)) {
        console.warn(`[ProtectedRoute] Access denied: Route is hidden for user type ${userType}`);
        return <Navigate to="/" replace />;
    }

    // PRIORITY 2: Check route-level bypass (from route config)
    if (bypass && Array.isArray(bypass) && bypass.includes(userType)) {
        return children; // Allow access if user type is in bypass list
    }

    // PRIORITY 3: Check user type restrictions if specified
    if (allowedUserTypes && Array.isArray(allowedUserTypes)) {
        if (!allowedUserTypes.includes(userType)) {
            return <Navigate to="/" replace />;
        }
    }

    // PRIORITY 4: Check if this module/submodule should BYPASS permission checks (from menu config)
    if (shouldBypassPermissionForModule(module, submodule, userType)) {
        return children;
    }

    // If no module specified, allow access (for common routes like profile, settings, etc.)
    if (!module) {
        return children;
    }

    // If permissions are not loaded yet, show loading or redirect
    // This prevents access before permissions are checked
    if (!permissions) {
        // You can return a loading spinner here if needed
        // For now, we'll allow it to prevent infinite loading
        return children;
    }

    const modulePermissions = permissions[module];
    
    // If module doesn't exist in permissions, deny access
    if (!modulePermissions) {
        console.warn(`[ProtectedRoute] Access denied: No permissions found for module "${module}"`);
        return <Navigate to="/" replace />;
    }

    // If submodule is specified, check submodule permissions
    if (submodule) {
        const submodulePermissions = modulePermissions[submodule];
        
        if (!submodulePermissions) {
            console.warn(`[ProtectedRoute] Access denied: No permissions found for submodule "${module}:${submodule}"`);
            return <Navigate to="/" replace />;
        }

        // Check for view permission
        const hasViewPermission = submodulePermissions.includes(`${submodule}-view`);
        if (!hasViewPermission) {
            console.warn(`[ProtectedRoute] Access denied: No view permission for "${module}:${submodule}"`);
            return <Navigate to="/" replace />;
        }
    }

    return children;
};

export default ProtectedRoute;
